/**
 * A peça aberta na análise preliminar (E78.F3.T08): o que é dela, e o quadro.
 *
 * Fica abaixo da tabela e mostra UMA peça por vez — no lugar dos N cards com o
 * formulário inteiro e o quadro completo empilhados. O que sobra de formulário
 * aqui são as observações da peça: o código declarado se edita na própria
 * linha da tabela, e os dois são gravados pelo mesmo caminho.
 */
import { Blueprint, Button, CardKicker, Field, MonoData, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

import { QuadroPorEixos } from './QuadroPorEixos';

const t = ptBR.exameEletronico;
const te = ptBR.exameEletronico.eixos;

export interface PecaSelecionadaProps {
  peca: ExameEletronicoCcbData;
  /** Posição na lista — vira a letra da peça quando não há número de CCB. */
  indice: number;
  total: number;
  observacoes: string;
  salvando: boolean;
  erro: string | null;
  sucesso: string | null;
  onEditarObservacoes: (valor: string) => void;
  onSalvar: () => void;
}

function Cabecalho({
  peca,
  indice,
  total,
  idPrefix,
}: {
  peca: ExameEletronicoCcbData;
  indice: number;
  total: number;
  idPrefix: string;
}): JSX.Element {
  return (
    <header className="space-y-sc-2">
      <CardKicker className="block">
        {t.ccbOrdinal.replace('{n}', String(indice + 1)).replace('{total}', String(total))}
      </CardKicker>
      <h2 className="text-display-sm text-fg" data-testid={`${idPrefix}-titulo`}>
        {te.pecaTitulo.replace('{peca}', tituloDaPeca(peca, indice))}
      </h2>
      <div>
        <p className="text-caption text-fg-dim">{t.codigoPecaLabel}</p>
        {peca.documento_content_sha256 === null ? (
          <p className="text-body-sm text-fg-muted">{t.codigoPecaAusente}</p>
        ) : (
          <MonoData as="code" className="break-all" data-testid={`${idPrefix}-sha256`}>
            {peca.documento_content_sha256}
          </MonoData>
        )}
      </div>
      {peca.exame?.status === 'concluido' ? (
        <p className="text-body-sm text-fg-muted" data-testid={`${idPrefix}-concluido`}>
          {t.concluidoAviso}
        </p>
      ) : null}
    </header>
  );
}

function Avisos({
  idPrefix,
  erro,
  sucesso,
}: {
  idPrefix: string;
  erro: string | null;
  sucesso: string | null;
}): JSX.Element {
  return (
    <>
      {erro !== null ? (
        <p
          className="border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid={`${idPrefix}-erro`}
        >
          {erro}
        </p>
      ) : null}
      {sucesso !== null ? (
        <p
          className="border border-success/30 bg-success-50 px-sc-3 py-sc-2 text-body-sm text-success-700"
          role="status"
          data-testid={`${idPrefix}-sucesso`}
        >
          {sucesso}
        </p>
      ) : null}
    </>
  );
}

export function PecaSelecionada({
  peca,
  indice,
  total,
  observacoes,
  salvando,
  erro,
  sucesso,
  onEditarObservacoes,
  onSalvar,
}: PecaSelecionadaProps): JSX.Element {
  const idPrefix = `peca-${peca.source_document_id}`;
  const concluido = peca.exame?.status === 'concluido';
  const resultado = peca.exame?.resultado ?? null;

  return (
    <Blueprint
      as="article"
      className="space-y-sc-4 p-sc-6"
      data-testid="peca-selecionada"
      data-source-document={peca.source_document_id}
    >
      <Cabecalho peca={peca} indice={indice} total={total} idPrefix={idPrefix} />
      <Avisos idPrefix={idPrefix} erro={erro} sucesso={sucesso} />

      <Field htmlFor={`${idPrefix}-observacoes`} label={t.observacoesDaPeca}>
        <Textarea
          id={`${idPrefix}-observacoes`}
          data-testid={`${idPrefix}-observacoes`}
          rows={2}
          value={observacoes}
          disabled={salvando || concluido}
          maxLength={2000}
          onChange={(evento) => onEditarObservacoes(evento.target.value)}
        />
      </Field>
      <Button
        type="button"
        variant="secondary"
        disabled={salvando || concluido}
        onClick={onSalvar}
        data-testid={`${idPrefix}-salvar-dados`}
      >
        {salvando ? t.form.salvando : t.salvarCodigoDaPeca}
      </Button>

      {resultado === null ? (
        <p className="text-body-sm text-fg-dim" data-testid={`${idPrefix}-sem-quadro`}>
          {t.quadro.semResultado}
        </p>
      ) : (
        <QuadroPorEixos resultado={resultado} tendencia={peca.exame?.tendencia ?? null} />
      )}
    </Blueprint>
  );
}
